/**
 * Free Shipping Progress Bar
 * + Oferta dinámica Botella Biostream
 * + Campaña BOTELLA30 controlada por Vercel
 * + Agregado directo al carrito
 * + Prevención de adiciones automáticas duplicadas
 * + Respeta botellas adicionales elegidas por el cliente
 * + Vista reducida durante la visita
 */

if (!customElements.get('free-shipping-progress-bar')) {
  let currentPercent = 0;
  let bottleAdditionInProgress = false;
  let campaignStateCache = null;
  let campaignStateExpiresAt = 0;

  class FreeShippingProgressBar extends HTMLElement {
    constructor() {
      super();
      this.bottleHandle = 'botella-termica-biostream-800ml-de-acero-inoxidable';
      this.bottleSku = 'BS800N01';
      this.bottleUrl = this.dataset.bottleUrl || '/products/' + this.bottleHandle;
      this.statusUrl = this.dataset.promoStatusUrl || '';
      this.campaignCode = 'BOTELLA30';
      this.campaignMinimum = 4000000;
      this.threshold50 = 5000000;
      this.threshold100 = 10000000;
      this.threshold200 = 20000000;
      this.storageKey = 'biostreamBottleOfferCollapsed';
      this.refreshTimer = null;
    }

    connectedCallback() {
      this.bindBottleButton();
      this.bindVariantSelector();
      this.bindCompactControls();
      this.restoreCompactState();
      this.boundRefresh = () => this.scheduleRefresh();
      window.addEventListener('biostream:cart-change', this.boundRefresh);
      window.addEventListener('pageshow', this.boundRefresh);
      this.updateStatus();
    }

    disconnectedCallback() {
      window.removeEventListener('biostream:cart-change', this.boundRefresh);
      window.removeEventListener('pageshow', this.boundRefresh);
      window.clearTimeout(this.refreshTimer);
    }

    bindBottleButton() {
      const button = this.querySelector('.biostream-bottle-offer__button');
      if (!button || button.dataset.biostreamBound === 'true') return;
      button.dataset.biostreamBound = 'true';
      button.addEventListener('click', () => this.addBottleToCart(false));
    }

    bindVariantSelector() {
      const options = this.querySelectorAll('.biostream-bottle-offer__variant-option');
      options.forEach((option) => {
        if (option.dataset.biostreamBound === 'true') return;
        option.dataset.biostreamBound = 'true';
        option.addEventListener('click', () => {
          options.forEach((item) => {
            const isSelected = item === option;
            item.classList.toggle('is-selected', isSelected);
            item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
          const image = this.querySelector('.biostream-bottle-offer__image');
          const imageLink = this.querySelector('.biostream-bottle-offer__image-link');
          const variantImage = option.dataset.variantImage;
          const variantTitle = option.dataset.variantTitle || '';
          if (image && variantImage) {
            image.src = variantImage;
            image.removeAttribute('srcset');
            image.alt = 'Botella Biostream ' + variantTitle;
          }
          if (imageLink && option.dataset.variantId) {
            imageLink.href = this.bottleUrl + '?variant=' + option.dataset.variantId;
          }
        });
      });
    }

    setVariantSelectorVisible(isVisible) {
      const selector = this.querySelector('.biostream-bottle-offer__variants');
      if (selector) selector.style.display = isVisible ? 'flex' : 'none';
    }

    bindCompactControls() {
      const dismissButton = this.querySelector('.biostream-bottle-offer__dismiss');
      const viewButton = this.querySelector('.biostream-bottle-offer__view');
      if (dismissButton && dismissButton.dataset.biostreamBound !== 'true') {
        dismissButton.dataset.biostreamBound = 'true';
        dismissButton.addEventListener('click', () => this.collapseOffer());
      }
      if (viewButton && viewButton.dataset.biostreamBound !== 'true') {
        viewButton.dataset.biostreamBound = 'true';
        viewButton.addEventListener('click', () => this.expandOffer());
      }
    }

    collapseOffer() {
      const offer = this.querySelector('.biostream-bottle-offer');
      if (!offer) return;
      offer.classList.add('is-collapsed');
      try {
        window.sessionStorage.setItem(this.storageKey, 'true');
      } catch (error) {
        console.warn('No fue posible recordar el estado del beneficio:', error);
      }
    }

    expandOffer() {
      const offer = this.querySelector('.biostream-bottle-offer');
      if (!offer) return;
      offer.classList.remove('is-collapsed');
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        console.warn('No fue posible actualizar el estado del beneficio:', error);
      }
    }

    restoreCompactState() {
      const offer = this.querySelector('.biostream-bottle-offer');
      if (!offer) return;
      try {
        const isCollapsed = window.sessionStorage.getItem(this.storageKey) === 'true';
        offer.classList.toggle('is-collapsed', isCollapsed);
      } catch (error) {
        offer.classList.remove('is-collapsed');
      }
    }

    productLink() {
      return '<a class="biostream-bottle-offer__product-link" href="' + this.bottleUrl + '">Botella Biostream</a>';
    }

    getShopRoot() {
      return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    }

    isBottleItem(item) {
      const itemHandle = item.handle || '';
      const itemUrl = item.url || '';
      const itemSku = item.sku || '';
      return itemHandle === this.bottleHandle ||
        itemSku === this.bottleSku ||
        itemUrl.includes('/products/' + this.bottleHandle);
    }

    isCampaignBottle(item) {
      return this.isBottleItem(item) &&
        item.properties &&
        item.properties._promo_biostream_bottle === 'campaign30';
    }

    cartDrawerHasBottle() {
      const cartDrawer = document.querySelector('#CartDrawer-CartItems');
      if (!cartDrawer) return false;
      const bottleLink = cartDrawer.querySelector('a[href*="/products/' + this.bottleHandle + '"]');
      if (bottleLink) return true;
      return Array.from(cartDrawer.querySelectorAll('.cart-item__name')).some((element) =>
        element.textContent.toLowerCase().includes('botella térmica biostream')
      );
    }

    wait(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }

    async jsonFetch(path, options = {}) {
      const response = await fetch(this.getShopRoot() + path, {
        cache: 'no-store',
        ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) }
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Shopify respondió ' + response.status);
      }
      return await response.json();
    }

    getCart() {
      return this.jsonFetch('cart.js');
    }

    async getCampaignState() {
      if (!this.statusUrl || this.statusUrl.includes('REEMPLAZAR-POR-TU-PROYECTO')) {
        throw new Error('Falta configurar el estado de la campaña');
      }
      if (campaignStateCache && Date.now() < campaignStateExpiresAt) {
        return campaignStateCache;
      }
      const response = await fetch(this.statusUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Estado de campaña no disponible');
      campaignStateCache = await response.json();
      campaignStateExpiresAt = Date.now() + 15000;
      return campaignStateCache;
    }

    qualifyingTotal(cart) {
      return cart.items.reduce((total, item) => {
        if (this.isBottleItem(item)) return total;
        if (typeof item.final_line_price === 'number') return total + item.final_line_price;
        if (typeof item.original_line_price === 'number') return total + item.original_line_price;
        return total;
      }, 0);
    }

    formatCLP(amountInCents) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0
      }).format(amountInCents / 100);
    }

    async getBottleVariant() {
      const product = await this.jsonFetch('products/' + this.bottleHandle + '.js');
      const selectedOption = this.querySelector('.biostream-bottle-offer__variant-option.is-selected');
      const selectedVariantId = selectedOption ? parseInt(selectedOption.dataset.variantId, 10) : 0;
      const selectedVariant = product.variants.find(
        (variant) => variant.id === selectedVariantId && variant.available
      );
      if (selectedVariant) return selectedVariant;
      const preferredVariant = product.variants.find(
        (variant) => variant.sku === this.bottleSku && variant.available
      );
      if (preferredVariant) return preferredVariant;
      const availableVariant = product.variants.find((variant) => variant.available);
      if (!availableVariant) throw new Error('La botella no tiene variantes disponibles');
      return availableVariant;
    }

    setOfferClass(className) {
      const offer = this.querySelector('.biostream-bottle-offer');
      if (!offer) return;
      offer.classList.remove(
        'biostream-bottle-offer--locked',
        'biostream-bottle-offer--10k',
        'biostream-bottle-offer--5k',
        'biostream-bottle-offer--free',
        'biostream-bottle-offer--added',
        'biostream-bottle-offer--error'
      );
      offer.classList.add(className);
    }

    async applyCampaignCode(cart) {
      const currentCodes = (cart.discount_codes || [])
        .filter((entry) => entry.applicable !== false)
        .map((entry) => entry.code)
        .filter(Boolean);
      if (currentCodes.some((code) => code.toUpperCase() === this.campaignCode)) return;
      const discount = [...new Set([...currentCodes, this.campaignCode])].join(',');
      const updatedCart = await this.jsonFetch('cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount })
      });
      const applied = (updatedCart.items || [])
        .filter((item) => this.isBottleItem(item))
        .some((item) => {
          const originalUnitPrice = item.original_price || item.price || 0;
          const totalDiscount = item.total_discount || 0;
          return item.final_line_price === 0 || totalDiscount >= originalUnitPrice;
        });
      if (!applied) throw new Error('BOTELLA30 ya no está disponible');
    }

    async removeCampaignBottles() {
      const cart = await this.getCart();
      for (const item of cart.items.filter((line) => this.isCampaignBottle(line))) {
        await this.jsonFetch('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      }
    }

    async addBottleToCart(isAutomatic) {
      if (bottleAdditionInProgress) return;
      bottleAdditionInProgress = true;
      const title = this.querySelector('.biostream-bottle-offer__title');
      const text = this.querySelector('.biostream-bottle-offer__text');
      const button = this.querySelector('.biostream-bottle-offer__button');
      const originalButtonText = button ? button.textContent : '';
      let addedCampaignBottle = false;

      if (button) {
        button.disabled = true;
        if (!isAutomatic) button.textContent = 'Agregando...';
      }

      try {
        await this.wait(300);
        const [cart, campaign] = await Promise.all([this.getCart(), this.getCampaignState()]);
        const qualifyingTotal = this.qualifyingTotal(cart);
        const existingBottle = cart.items.find((item) => this.isBottleItem(item));

        if (existingBottle || this.cartDrawerHasBottle()) {
          if (campaign.active && qualifyingTotal >= this.campaignMinimum) {
            await this.applyCampaignCode(cart);
          }
          if (title) title.innerHTML = '✓ ' + this.productLink() + ' agregada';
          if (text) text.textContent = 'La botella ya está en tu carrito.';
          if (button) button.style.display = 'none';
          this.setVariantSelectorVisible(false);
          this.setOfferClass('biostream-bottle-offer--added');
          bottleAdditionInProgress = false;
          return;
        }

        const campaignEligible = campaign.active && qualifyingTotal >= this.campaignMinimum;
        const standardEligible = !campaign.active && qualifyingTotal >= this.threshold50;
        if (!campaignEligible && !standardEligible) {
          throw new Error('El carrito todavía no cumple el mínimo');
        }

        if (isAutomatic) {
          if (title) title.innerHTML = '🎁 ¡Desbloqueaste tu ' + this.productLink() + ' gratis!';
          if (text) text.textContent = 'La estamos agregando automáticamente al carrito...';
          if (button) button.style.display = 'none';
        }

        const variant = await this.getBottleVariant();
        await this.wait(250);
        const finalCheckCart = await this.getCart();
        const bottleAppeared = finalCheckCart.items.some((item) => this.isBottleItem(item)) ||
          this.cartDrawerHasBottle();
        if (bottleAppeared) {
          if (campaignEligible) await this.applyCampaignCode(finalCheckCart);
          bottleAdditionInProgress = false;
          window.setTimeout(() => window.location.reload(), 200);
          return;
        }

        await this.jsonFetch('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: variant.id,
              quantity: 1,
              properties: {
                _promo_biostream_bottle: campaignEligible ? 'campaign30' : 'regular'
              }
            }]
          })
        });
        addedCampaignBottle = campaignEligible;
        if (campaignEligible) {
          const refreshedCart = await this.getCart();
          await this.applyCampaignCode(refreshedCart);
        }

        if (title) {
          title.innerHTML =
            (isAutomatic || campaignEligible ? '🎁 ¡Tu ' : '✓ ') +
            this.productLink() +
            (isAutomatic || campaignEligible ? ' GRATIS fue agregada!' : ' agregada');
        }
        if (text) text.textContent = 'La botella ya está en tu carrito.';
        if (button) button.style.display = 'none';
        this.setVariantSelectorVisible(false);
        this.setOfferClass(
          isAutomatic || campaignEligible
            ? 'biostream-bottle-offer--free'
            : 'biostream-bottle-offer--added'
        );
        window.dispatchEvent(new CustomEvent('biostream:cart-change'));
        window.setTimeout(() => window.location.reload(), 500);
      } catch (error) {
        if (addedCampaignBottle) await this.removeCampaignBottles().catch(() => {});
        campaignStateCache = null;
        campaignStateExpiresAt = 0;
        console.warn('Agregar Botella Biostream:', error);
        this.setOfferClass('biostream-bottle-offer--error');
        if (title) title.textContent = 'No pudimos agregar la botella';
        if (text) text.textContent = 'Inténtalo nuevamente en unos segundos.';
        if (button) {
          button.disabled = false;
          button.style.display = 'inline-flex';
          button.textContent = 'Intentar nuevamente';
          window.setTimeout(() => {
            button.textContent = originalButtonText || 'Llévala';
          }, 2500);
        }
        bottleAdditionInProgress = false;
      }
    }

    async updateStatus() {
      const remainingAmountText = this.querySelector('.free-shipping__info-remaining strong');
      const cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const shippingAmount = parseInt(this.dataset.shippingAmount, 10) || 0;
      const currencyRate = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
        ? window.Shopify.currency.rate
        : 1;
      const freeShippingThreshold = Math.round(shippingAmount * currencyRate);
      const remainingInfo = this.querySelector('.free-shipping__info-remaining');
      const eligibilityInfo = this.querySelector('.free-shipping__info-eligibility');
      const isEligible = cartTotal >= freeShippingThreshold;
      if (remainingAmountText && !isEligible) {
        const remainingAmount = freeShippingThreshold - cartTotal;
        const currencyFormat = window.theme && window.theme.settings &&
          window.theme.settings.money_with_currency_format
          ? window.theme.settings.money_with_currency_format
          : '${{amount}}';
        if (typeof formatMoney === 'function') {
          remainingAmountText.innerHTML = formatMoney(remainingAmount, currencyFormat);
        }
      }
      if (remainingInfo) remainingInfo.style.display = isEligible ? 'none' : 'block';
      if (eligibilityInfo) eligibilityInfo.style.display = isEligible ? 'block' : 'none';
      const progressPercentage = freeShippingThreshold > 0
        ? (isEligible ? 1 : cartTotal / freeShippingThreshold)
        : 1;
      this.animateProgressBar(progressPercentage);
      await this.updateBottleOffer();
    }

    async updateBottleOffer() {
      const offer = this.querySelector('.biostream-bottle-offer');
      const title = this.querySelector('.biostream-bottle-offer__title');
      const text = this.querySelector('.biostream-bottle-offer__text');
      const button = this.querySelector('.biostream-bottle-offer__button');
      if (!offer || !title || !text || !button) return;

      let cart;
      let campaign;
      try {
        [cart, campaign] = await Promise.all([this.getCart(), this.getCampaignState()]);
      } catch (error) {
        console.warn('Oferta Botella Biostream:', error);
        offer.style.display = 'flex';
        this.setOfferClass('biostream-bottle-offer--error');
        title.textContent = 'No pudimos actualizar el beneficio';
        text.textContent = 'Actualiza la página para intentarlo nuevamente.';
        button.style.display = 'none';
        this.setVariantSelectorVisible(false);
        return;
      }

      const bottleAlreadyExists = cart.items.some((item) => this.isBottleItem(item)) ||
        this.cartDrawerHasBottle();
      const qualifyingTotal = this.qualifyingTotal(cart);
      offer.style.display = 'flex';
      button.disabled = false;

      if (campaign.active) {
        this.renderCampaignOffer({
          campaign, cart, qualifyingTotal, bottleAlreadyExists, title, text, button
        });
        return;
      }
      this.renderStandardOffer({ qualifyingTotal, bottleAlreadyExists, title, text, button });
    }

    renderCampaignOffer({ campaign, cart, qualifyingTotal, bottleAlreadyExists, title, text, button }) {
      if (qualifyingTotal >= this.campaignMinimum) {
        this.setOfferClass('biostream-bottle-offer--free');
        title.innerHTML = bottleAlreadyExists
          ? '🎁 ¡Tu ' + this.productLink() + ' queda GRATIS!'
          : '🎁 ¡Desbloqueaste una ' + this.productLink() + ' GRATIS!';
        text.innerHTML = bottleAlreadyExists
          ? 'La botella ya está en tu carrito. Quedan <strong>' + campaign.remaining +
            ' de ' + campaign.limit + '</strong> beneficios.'
          : 'Primeras 30 compras pagadas desde <strong>$40.000</strong>. Quedan <strong>' +
            campaign.remaining + '</strong> beneficios.';
        if (bottleAlreadyExists) {
          button.style.display = 'none';
          this.setVariantSelectorVisible(false);
          this.applyCampaignCode(cart).catch((error) => {
            campaignStateCache = null;
            campaignStateExpiresAt = 0;
            console.warn('No se pudo aplicar BOTELLA30:', error);
            this.scheduleRefresh();
          });
        } else {
          button.textContent = 'Agregar botella GRATIS';
          button.style.display = 'inline-flex';
          this.setVariantSelectorVisible(true);
        }
        return;
      }

      this.setOfferClass('biostream-bottle-offer--locked');
      title.textContent = '🎁 Primeras 30 compras';
      text.innerHTML = 'Te faltan <strong>' +
        this.formatCLP(this.campaignMinimum - qualifyingTotal) +
        '</strong> para llevarte una ' + this.productLink() + ' GRATIS.';
      button.style.display = 'none';
      this.setVariantSelectorVisible(false);
    }

    renderStandardOffer({ qualifyingTotal, bottleAlreadyExists, title, text, button }) {
      if (qualifyingTotal >= this.threshold200) {
        this.setOfferClass('biostream-bottle-offer--free');
        title.innerHTML = bottleAlreadyExists
          ? '🎁 ¡Te llevas una ' + this.productLink() + ' GRATIS!'
          : '🎁 ¡Desbloqueaste una ' + this.productLink() + ' GRATIS!';
        text.textContent = bottleAlreadyExists
          ? 'La botella ya está en tu carrito.'
          : 'La estamos agregando automáticamente al carrito...';
        button.style.display = 'none';
        this.setVariantSelectorVisible(false);
        if (!bottleAlreadyExists) this.addBottleToCart(true);
        return;
      }

      if (bottleAlreadyExists) {
        this.setOfferClass('biostream-bottle-offer--added');
        title.innerHTML = '✓ ' + this.productLink() + ' agregada';
        text.textContent = 'La botella ya está en tu carrito.';
        button.style.display = 'none';
        this.setVariantSelectorVisible(false);
        return;
      }

      if (qualifyingTotal >= this.threshold100) {
        this.setOfferClass('biostream-bottle-offer--5k');
        title.textContent = '🎁 Beneficio desbloqueado';
        text.innerHTML = 'Puedes llevar una ' + this.productLink() +
          ' por solo <strong>$4.990</strong>.';
        button.textContent = 'Llévala por $4.990';
        button.style.display = 'inline-flex';
        this.setVariantSelectorVisible(true);
        return;
      }

      if (qualifyingTotal >= this.threshold50) {
        this.setOfferClass('biostream-bottle-offer--10k');
        title.textContent = '🎁 Beneficio desbloqueado';
        text.innerHTML = 'Puedes llevar una ' + this.productLink() +
          ' por solo <strong>$9.900</strong>.';
        button.textContent = 'Llévala por $9.900';
        button.style.display = 'inline-flex';
        this.setVariantSelectorVisible(true);
        return;
      }

      this.setOfferClass('biostream-bottle-offer--locked');
      title.textContent = 'Tu próximo beneficio';
      text.innerHTML = 'Te faltan <strong>' +
        this.formatCLP(this.threshold50 - qualifyingTotal) +
        '</strong> para desbloquear tu ' + this.productLink() +
        ' por <strong>$9.900</strong>.';
      button.style.display = 'none';
      this.setVariantSelectorVisible(false);
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.updateStatus(), 350);
    }

    animateProgressBar(progressPercentage) {
      const targetPercent = Math.max(0, Math.min(progressPercentage * 100, 100));
      const startPercent = currentPercent;
      const duration = 200;
      const startTime = performance.now();
      const ease = (time) => time * (2 - time);
      const animate = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = ease(progress);
        currentPercent = startPercent + (targetPercent - startPercent) * easedProgress;
        this.style.setProperty('--percent', currentPercent + '%');
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          currentPercent = targetPercent;
          this.style.setProperty('--percent', currentPercent + '%');
        }
      };
      requestAnimationFrame(animate);
    }
  }

  customElements.define('free-shipping-progress-bar', FreeShippingProgressBar);
}
